<template>
    <BaseDialog
        v-if="testimonial"
        title=" "
        :loading="false"
        v-model:visible="visible"
        :size="DialogSize.customSize('50vw')"
        :buttonClose="false"
    >
        <template v-slot:content>
            <div class="flex flex-col min-h-[20rem] h-full justify-center">
                <ProgressSpinner class="" v-if="isLoading" />

                <div v-if="testimonial">
                    <div class="w-full h-full">
                        <div class="bg-black rounded-md aspect-video">
                            <video
                                ref="videoPlayer"
                                v-if="videoUrl"
                                class="w-full h-full object-contain rounded-md"
                                controls
                                autoplay
                            >
                                <source :src="videoUrl" />
                            </video>
                        </div>

                        <div class="flex flex-row flex-wrap my-4">
                            <div class="flex flex-row pr-4">
                                <div class="grid items-center">
                                    <VisibilityIcon :testimonial="testimonial"></VisibilityIcon>
                                </div>
                                <div class="grid items-end">
                                    <div class="text-4xl leading-none">{{ testimonial.title }}</div>
                                </div>
                            </div>

                            <div class="flex flex-row items-end gap-2">
                                <Tag severity="info">
                                    <span v-tooltip="'Author'">{{ testimonial.displayName() }}</span>

                                    <img
                                        v-if="testimonial.author?.country"
                                        src="https://primefaces.org/cdn/primevue/images/flag/flag_placeholder.png"
                                        :class="`mr-2 flag flag-${testimonial.author.country.value.toLowerCase()}`"
                                        style="width: 18px"
                                        v-tooltip="testimonial.author.country.label"
                                    />
                                </Tag>

                                <Tag severity="secondary" v-tooltip="'Creation date'">
                                    {{ testimonial.formattedDate() }}</Tag
                                >
                                <div v-if="testimonial.products?.length" class="flex flex-row flex-wrap gap-2">
                                    <Tag
                                        v-for="(item, index) in testimonial.products"
                                        :key="index"
                                        class="text-xs"
                                        severity="success"
                                        >{{ item.name }}</Tag
                                    >
                                </div>
                            </div>
                        </div>
                        <Card>
                            <template #content>
                                <div class="flex flex-row justify-between items-start">
                                    <p class="m-0 flex-1">
                                        {{ testimonial.description }}
                                    </p>
                                    <div>
                                        <Button
                                            icon="pi pi-ellipsis-v"
                                            class="p-button-text"
                                            @click="toggleMenu"
                                            aria-label="Options"
                                        />
                                        <Menu ref="menu" :model="menuItems" :popup="true" />
                                    </div>
                                </div>
                                <Divider align="left" type="solid">
                                    <b>Industry</b>
                                </Divider>
                                <div v-if="testimonial.industries.length" class="flex flex-row flex-wrap gap-2">
                                    <Chip
                                        v-for="(item, index) in testimonial.industries"
                                        :key="index"
                                        :label="item.name"
                                        class="text-xs"
                                    />
                                </div>
                                <Divider align="left" type="solid">
                                    <b>Tags</b>
                                </Divider>
                                <div v-if="testimonial.tags.length" class="flex flex-row flex-wrap gap-2">
                                    <Chip
                                        v-for="(item, index) in testimonial.tags"
                                        :key="index"
                                        :label="item"
                                        class="text-xs"
                                    />
                                </div>
                            </template>
                        </Card>
                    </div>
                </div>
            </div>
        </template>
        <template v-slot:okButton>
            <Button
                v-if="testimonial.canShare() && authService.hasPermission(PermissionsEnum.shareTestimonial)"
                icon="pi pi-clone"
                iconPos="right"
                label="Share"
                @click="copyShare"
                v-tooltip="'Copy share link'"
            ></Button>
        </template>
    </BaseDialog>
    <ConfirmDialog :draggable="false" :visible="confirmationVisible"></ConfirmDialog>

    <!-- Add share creationg link -->
    <!-- <ShareDialog v-if="shareVisible" v-model:visible="shareVisible"></ShareDialog> -->
</template>

<script setup>
import ProgressSpinner from "primevue/progressspinner";
import BaseDialog from "@/shared/components/BaseDialog.vue";
import { DialogSize } from "@/core/enums/DialogSizeEnum";
import { ref, defineProps, defineEmits, inject, onMounted, nextTick, defineModel } from "vue";
import { Divider, Tag, Chip } from "primevue";
import Card from "primevue/card";
import VisibilityIcon from "@/shared/common/VisibilityIcon.vue";
import { Button } from "primevue";
import { useToast } from "primevue/usetoast";
import { PermissionsEnum } from "@/core/enums/PermissionsEnum";
import Menu from "primevue/menu";
import { useConfirm } from "primevue/useconfirm";
import { TestimonialStatusEnum } from "@/core/enums/TestimonialStatusEnum";
import emitter from "@/boot/eventBus";
import { Events } from "@/core/enums/EventsEnum";

const visible = defineModel("visible");

const emit = defineEmits(["authorCreated"]);
const testimonialService = inject("testimonialService");
const authService = inject("authService");
const toast = useToast();
const confirm = useConfirm();

const isLoading = ref(true);
const testimonial = ref();
const videoUrl = ref(null);
const shareVisible = ref(false);
const videoPlayer = ref(null);
const menu = ref(null);
const confirmationVisible = ref(false);

const props = defineProps({
    testimonialId: { type: String },
});

const menuItems = [
    {
        label: "Edit",
        icon: "pi pi-pencil",
        command: () => {
            emit("update:visible", false);
            nextTick(() => {
                emitter.emit(Events.editTestimonial, { id: props.testimonialId });
            });
        },
    },
    {
        label: "Archive",
        icon: "pi pi-trash",
        command: () => archiveConfirm(),
    },
];

const toggleMenu = (event) => {
    menu.value.toggle(event);
};

onMounted(async () => {
    try {
        const detail = await testimonialService.getDetails(props.testimonialId);
        testimonial.value = detail;
        getUrl();
    } catch (error) {
        console.log(error);
    } finally {
        isLoading.value = false;
    }
});

const getUrl = async () => {
    const playKey = await testimonialService.getPlayToken(testimonial.value);
    videoUrl.value = `${process.env.VUE_APP_API_URL}/v1/testimonial/${testimonial.value.id}/video?playToken=${playKey.key}`;
};

const copyShare = async () => {
    await navigator.clipboard.writeText(testimonial.value.generateShareLink());
    toast.add({ severity: "success", detail: "Share link copied!", summary: "Testimonial", life: 4000 });
};

const archiveConfirm = () => {
    confirmationVisible.value = true;
    confirm.require({
        message: "This can't be undone",
        header: "Archive Confirmation",
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: "Cancel",
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            label: "Archive",
            severity: "danger",
        },
        accept: async () => {
            try {
                await testimonialService.update(testimonial.value.id, {
                    status: TestimonialStatusEnum.archived,
                });

                toast.add({
                    severity: "success",
                    summary: "Archived",
                    detail: "Testimonial archived",
                    life: 3000,
                });
                confirmationVisible.value = false;

                nextTick(() => {
                    emitter.emit(Events.testimonialRemove, { id: props.testimonialId });
                });
            } catch (error) {}
        },
        reject: () => {
            confirmationVisible.value = false;
        },
        onHide: () => {
            confirmationVisible.value = false;
        },
    });
};
</script>

<style scoped>
@import "../../../styles/flags.css";
</style>
